import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "../services/apiClient";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  UserOutlined,
  LockOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  MailOutlined,
  PhoneOutlined,
} from "@ant-design/icons";
import { ThemeContext } from "../context/ThemeContext";
import { useUser } from "../context/UserContext";
import { getPostLoginPath } from "../utils/roleUtils";
import "./SignUpModern.css";
import { message } from "antd";
import GoogleOAuthButton from "./GoogleOAuthButton";
import AppleOAuthButton from "./AppleOAuthButton";

const SignUpModern = () => {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useContext(ThemeContext);
  const { clearUserData, applyAuthResponse, forceRefreshUserData } = useUser();

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordValidation, setPasswordValidation] = useState({
    length: false,
    upperCase: false,
    lowerCase: false,
    number: false,
    specialChar: false,
  });
  const [hasStartedTyping, setHasStartedTyping] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState(0);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNo: "",
    email: "",
    password: "",
    confirmPassword: "",
  });

  const baseUrl = process.env.REACT_APP_AUTH_URL;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate password when password field changes
    if (name === "password") {
      if (value.length > 0) {
        setHasStartedTyping(true);
      }
      validatePassword(value);
    }
  };

  const validatePassword = (password) => {
    const validation = {
      length: password.length >= 8 && password.length <= 15,
      upperCase: /[A-Z]/.test(password),
      lowerCase: /[a-z]/.test(password),
      number: /\d/.test(password),
      specialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
    };
    setPasswordValidation(validation);

    // Calculate password strength (0-4)
    let strength = 0;
    if (validation.length) strength++;
    if (validation.upperCase) strength++;
    if (validation.lowerCase) strength++;
    if (validation.number) strength++;
    if (validation.specialChar) strength++;

    setPasswordStrength(strength);
  };


  const validateForm = () => {
    if (!formData.firstName.trim()) {
      message.error("First name is required");
      return false;
    }
    if (!formData.lastName.trim()) {
      message.error("Last name is required");
      return false;
    }
    if (!formData.email.trim()) {
      message.error("Email is required");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      message.error("Please enter a valid email address");
      return false;
    }
    if (!formData.password) {
      message.error("Password is required");
      return false;
    }
    if (formData.password.length < 8) {
      message.error("Password must be at least 8 characters long");
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      message.error("Passwords do not match");
      return false;
    }
    if (!formData.mobileNo.trim()) {
      message.error("Mobile number is required");
      return false;
    }
    if (!/^\+?[\d\s\-\(\)]+$/.test(formData.mobileNo)) {
      message.error("Please enter a valid mobile number");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const signupData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        mobileNo: formData.mobileNo,
      };

      console.log("Signup Data:", signupData);
      const response = await apiClient.post(`${baseUrl}/user/signup`, signupData);
      console.log("Signup Response:", response.data);

      // Check if signup was successful and requires verification
      if (response.data.success && response.data.data && response.data.data.requiresVerification) {
        clearUserData();
        message.success("Registration successful! Please check your email for verification code.");

        setIsLoading(false);
        // Navigate to OTP verification page
        navigate("/verify-otp", {
          state: {
            email: formData.email,
            fromSignup: true,
            message: response.data.data.message || "Please verify your email to complete registration."
          }
        });
      } else {
        // Fallback for old response format
        message.success("Registration successful! Please Login.");

        setIsLoading(false);
        navigate("/login");
      }
    } catch (error) {
      console.error("Signup Error:", error);
      setIsLoading(false);

      let errorMessage = "Registration failed. Please try again.";

      if (error.response) {
        // Handle user already exists error (409 Conflict)
        if (error.response.status === 409 && error.response.data.error) {
          message.error(error.response.data.error);
          return;
        }

        // Handle other validation errors (400 Bad Request)
        if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      } else if (error.request) {
        errorMessage = "Network error. Please check your connection.";
      }

      message.error(errorMessage);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  const handleOAuthSuccess = (userData) => {
    applyAuthResponse(userData);
    forceRefreshUserData();
    message.success("Registration successful! You are now logged in.");

    setTimeout(() => {
      navigate(getPostLoginPath(userData.roles || []), {
        state: {
          show2FAPopup: true,
          email: userData.email,
        },
      });
    }, 500);
  };

  const handleOAuthError = (error) => {
    console.error('OAuth error:', error);
  };

  return (
    <div className={`signup-modern-container ${isDark ? "dark" : ""}`}>
      <div className="signup-content">
        <div className="signup-main-card">
          {/* Left Column - Signup Form */}
          <div className="signup-form-section">
            <div className="signup-card">

              {/* Header */}
              <div className="signup-header">
                <h2 className="welcome-title">Welcome to <strong>Simply FI</strong></h2>
                <p className="signup-subtitle">
                  Your gateway to intelligent bond investments. Discover, analyze, and invest in bonds with confidence using our advanced platform.
                </p>
              </div>

              {/* Form */}
              <form className="signup-form" onSubmit={handleSubmit}>
                {/* First Name Field */}
                <div className="form-group">
                  <div className="input-container">
                    <div className="input-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="12" cy="7" r="4" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="First Name"
                      required
                    />
                  </div>
                </div>

                {/* Last Name Field */}
                <div className="form-group">
                  <div className="input-container">
                    <div className="input-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <circle cx="12" cy="7" r="4" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Last Name"
                      required
                    />
                  </div>
                </div>

                {/* Mobile Number Field */}
                <div className="form-group">
                  <div className="input-container">
                    <div className="input-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 16.92V19.92C22.0011 20.1985 21.9441 20.4742 21.8325 20.7293C21.7209 20.9845 21.5573 21.2136 21.3521 21.4019C21.1468 21.5901 20.9046 21.7335 20.6407 21.8227C20.3769 21.9119 20.0974 21.9451 19.82 21.92C16.7428 21.5856 13.787 20.5341 11.19 18.85C8.77382 17.3147 6.72533 15.2662 5.18999 12.85C3.49997 10.2412 2.44824 7.27099 2.11999 4.18C2.095 3.90347 2.12787 3.62476 2.21649 3.36162C2.30512 3.09849 2.44756 2.85669 2.63476 2.65162C2.82196 2.44655 3.0498 2.28271 3.30379 2.17052C3.55777 2.05833 3.83233 2.00026 4.10999 2H7.10999C7.59531 1.99522 8.06679 2.16708 8.43376 2.48353C8.80073 2.79999 9.03996 3.23945 9.10999 3.72C9.23662 4.68007 9.47144 5.62273 9.80999 6.53C9.94454 6.88792 9.97366 7.27691 9.89391 7.65088C9.81415 8.02485 9.62886 8.36811 9.35999 8.64L8.08999 9.91C9.51355 12.4135 11.5865 14.4864 14.09 15.91L15.36 14.64C15.6319 14.3711 15.9751 14.1858 16.3491 14.1061C16.7231 14.0263 17.1121 14.0555 17.47 14.19C18.3773 14.5286 19.3199 14.7634 20.28 14.89C20.7658 14.9585 21.2094 15.2032 21.5265 15.5775C21.8437 15.9518 22.0122 16.4296 22 16.92Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <input
                      type="tel"
                      name="mobileNo"
                      value={formData.mobileNo}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Mobile number"
                      required
                    />
                  </div>
                </div>

                {/* Email Field */}
                <div className="form-group">
                  <div className="input-container">
                    <div className="input-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M4 4H20C21.1 4 22 4.9 22 6V18C22 19.1 21.1 20 20 20H4C2.9 20 2 19.1 2 18V6C2 4.9 2.9 4 4 4Z" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        <polyline points="22,6 12,13 2,6" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                      placeholder="Email Address"
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div className="form-group">
                  <div className="input-container password-input-container">
                    <div className="input-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#9CA3AF" strokeWidth="2" />
                        <circle cx="12" cy="16" r="1" fill="#9CA3AF" />
                        <path d="M7 11V7A5 5 0 0 1 17 7V11" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="form-input password-input"
                      placeholder="Password"
                      maxLength={15}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={togglePasswordVisibility}
                    >
                      {showPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M1 1l22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>

                {/* Confirm Password Field */}
                <div className="form-group">
                  <div className="input-container password-input-container">
                    <div className="input-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" stroke="#9CA3AF" strokeWidth="2" />
                        <circle cx="12" cy="16" r="1" fill="#9CA3AF" />
                        <path d="M7 11V7A5 5 0 0 1 17 7V11" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="form-input password-input"
                      placeholder="Confirm Password"
                      maxLength={15}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle-btn"
                      onClick={toggleConfirmPasswordVisibility}
                    >
                      {showConfirmPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M1 1l22 22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>


                <button type="submit" className="submit-btn" disabled={isLoading}>
                  {isLoading ? "Creating Account..." : "Sign Up"}
                </button>
              </form>

              {/* Divider */}
              <div className="divider">
                <span>or</span>
              </div>

              {/* Social Signup */}
              <div className="social-login">
                <GoogleOAuthButton
                  onSuccess={handleOAuthSuccess}
                  onError={handleOAuthError}
                  buttonText="Sign up with Google"
                />
                {/* Apple Sign In disabled - requires $99/year Apple Developer Program
                <AppleOAuthButton 
                  onSuccess={handleOAuthSuccess}
                  onError={handleOAuthError}
                  buttonText="Sign up with Apple"
                />
                */}
              </div>

              {/* Toggle Form */}
              <div className="toggle-form">
                <span className="toggle-text">
                  Already have an account? <strong onClick={() => navigate("/login")} style={{ cursor: 'pointer' }}>Login</strong>
                </span>
              </div>
            </div>
          </div>

          {/* Right Column - Password Guidelines */}
          <div className="password-guidelines-section">
            {/* Theme Toggle */}
            <button
              className="theme-toggle-btn"
              onClick={toggleTheme}
              title={`Switch to ${isDark ? "light" : "dark"} mode`}
            >
              {isDark ? "☀️" : "🌙"}
            </button>

            <div className="guidelines-card">
              <h3 className="guidelines-title">Guidelines for Creating a Strong Password</h3>
              <div className="guidelines-list">
                <div className="guideline-item">
                  <span className="guideline-number">1.</span>
                  <span>Use at least <strong>12-16 characters</strong>.</span>
                </div>
                <div className="guideline-item">
                  <span className="guideline-number">2.</span>
                  <span>Use <strong>A-Z, a-z, 0-9 & symbols</strong> (@, #, $, %, !)</span>
                </div>
                <div className="guideline-item">
                  <span className="guideline-number">3.</span>
                  <span>Avoid <strong>common info</strong> (names, dates, 123456).</span>
                </div>
                <div className="guideline-item">
                  <span className="guideline-number">4.</span>
                  <span>Join <strong>random words + symbols</strong> (e.g., Sun!River9*Book).</span>
                </div>
                <div className="guideline-item">
                  <span className="guideline-number">5.</span>
                  <span><strong>Don't reuse old passwords</strong> across different accounts.</span>
                </div>
                <div className="guideline-item">
                  <span className="guideline-number">6.</span>
                  <span>Try using a <strong>password manager</strong> to generate and store strong, random passwords.</span>
                </div>
              </div>

              {/* Password Requirements - Full Width */}
              <div className="password-requirements-full">
                {/* Password Strength Progress Bar */}
                <div className="password-strength-progress">
                  <div className="strength-header">
                    <span>Strength of password</span>
                  </div>
                  <div className="strength-bar">
                    <div className={`strength-segment ${passwordStrength >= 1 ? 'active' : ''} weak`}></div>
                    <div className={`strength-segment ${passwordStrength >= 2 ? 'active' : ''} moderate`}></div>
                    <div className={`strength-segment ${passwordStrength >= 3 ? 'active' : ''} strong`}></div>
                    <div className={`strength-segment ${passwordStrength >= 4 ? 'active' : ''} very-strong`}></div>
                  </div>
                  <div className="strength-labels">
                    <span className={`strength-label ${passwordStrength >= 1 ? 'active' : ''} weak`}>Weak</span>
                    <span className={`strength-label ${passwordStrength >= 2 ? 'active' : ''} moderate`}>Moderate</span>
                    <span className={`strength-label ${passwordStrength >= 3 ? 'active' : ''} strong`}>Strong</span>
                    <span className={`strength-label ${passwordStrength >= 4 ? 'active' : ''} very-strong`}>Very Strong</span>
                  </div>
                </div>

                {/* Requirements Grid */}
                <div className="requirements-grid">
                  <div className={`requirement ${hasStartedTyping ? (passwordValidation.upperCase ? 'valid' : 'invalid') : 'neutral'}`}>
                    <span className="requirement-icon">
                      {hasStartedTyping ? (passwordValidation.upperCase ? '✓' : '✗') : '•'}
                    </span>
                    At least one upper case letter
                  </div>
                  <div className={`requirement ${hasStartedTyping ? (passwordValidation.lowerCase ? 'valid' : 'invalid') : 'neutral'}`}>
                    <span className="requirement-icon">
                      {hasStartedTyping ? (passwordValidation.lowerCase ? '✓' : '✗') : '•'}
                    </span>
                    At least one lower case letter
                  </div>
                  <div className={`requirement ${hasStartedTyping ? (passwordValidation.number ? 'valid' : 'invalid') : 'neutral'}`}>
                    <span className="requirement-icon">
                      {hasStartedTyping ? (passwordValidation.number ? '✓' : '✗') : '•'}
                    </span>
                    At least one number
                  </div>
                  <div className={`requirement ${hasStartedTyping ? (passwordValidation.specialChar ? 'valid' : 'invalid') : 'neutral'}`}>
                    <span className="requirement-icon">
                      {hasStartedTyping ? (passwordValidation.specialChar ? '✓' : '✗') : '•'}
                    </span>
                    At least one special character
                  </div>
                </div>
              </div>
            </div>

            <div className="marketing-section">
              <div className="marketing-content">
                <div className="marketing-circles">
                  <div className="circle circle-1"></div>
                  <div className="circle circle-2"></div>
                </div>
                <div className="marketing-navigation">
                  <button className="nav-arrow left-arrow">←</button>
                  <button className="nav-arrow right-arrow">→</button>
                </div>
                <p className="marketing-text">
                  With precision control, you don't just invest—you invest with clarity, confidence, and strategic direction, ensuring your portfolio works exactly the way you want it to.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpModern;
